import React, { useEffect } from 'react';
import { Alert, Descriptions, Checkbox, Form } from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import CBModal from './CBModal';

/**
 * Phase 1 - Validation du dossier de déclaration.
 * Vérification CSS/SE des données essentielles avant de passer VALIDE.
 */
export default function ValidationModal({ open, onClose, onConfirm, sinistre, assure, loading }) {
  const { t } = useTranslation('commun');
  const [form] = Form.useForm();

  const fmt = (d) => d ? dayjs(d).format('DD/MM/YYYY') : '-';
  const fmtTime = (heure) => {
    if (!heure) return '-';
    const [hh, mm] = String(heure).split(':');
    return t('validationModal.heure', { hh, mm: mm || '00' });
  };

  const handleOk = async () => {
    try {
      await form.validateFields();
    } catch {
      return;
    }
    onConfirm();
  };

  useEffect(() => {
    if (!open) form.resetFields();
  }, [open, form]);

  return (
    <CBModal
      title={t('validationModal.titre')}
      open={open}
      onCancel={onClose}
      onOk={handleOk}
      okText={t('validationModal.titre')}
      cancelText={t('validationModal.annuler')}
      width={640}
      confirmLoading={loading}
      okButtonProps={{ style: { background: '#1a6b3c', borderColor: '#1a6b3c' } }}
      styles={{
        content: { border: '2px solid #1a6b3c', padding: 0 },
        header: { background: '#f0faf4', borderBottom: '1px solid #d1fae5', padding: '16px 24px' },
        body: { padding: '20px 24px' },
        footer: { padding: '12px 24px', borderTop: '1px solid #e5e7eb' },
      }}
    >
      <Alert
        type="info"
        showIcon
        icon={<CheckCircleOutlined />}
        message={t('validationModal.controle')}
        description={sinistre?.typeSinistre === 'SURVENU_ETRANGER'
          ? t('validationModal.descriptionTe')
          : t('validationModal.descriptionEt')}
        style={{ marginBottom: 16 }}
      />

      <Descriptions
        bordered
        size="small"
        column={1}
        labelStyle={{ width: 180, fontWeight: 500, background: '#fafafa' }}
      >
        <Descriptions.Item label={t('validationModal.numeroSinistre')}>{sinistre?.numeroSinistreLocal || '-'}</Descriptions.Item>
        <Descriptions.Item label={t('validationModal.typeSinistre')}>
          {sinistre?.typeSinistre === 'SURVENU_TOGO' ? t('validationModal.survenuTogo') : t('validationModal.survenuEtranger')}
        </Descriptions.Item>
        <Descriptions.Item label={t('validationModal.dateAccident')}>
          {fmt(sinistre?.dateAccident)}{sinistre?.heureAccident ? ` · ${fmtTime(sinistre.heureAccident)}` : ''}
        </Descriptions.Item>
        <Descriptions.Item label={t('validationModal.lieu')}>
          {[sinistre?.ville, sinistre?.lieuAccident].filter(Boolean).join(' - ') || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('validationModal.assure')}>
          {assure?.nomComplet || sinistre?.assureNomComplet || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('validationModal.vehicule')}>
          {[assure?.marqueVehicule, assure?.immatriculation].filter(Boolean).join(' · ') || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('validationModal.assureur')}>{sinistre?.organismeMembreRaisonSociale || '-'}</Descriptions.Item>
        <Descriptions.Item label={t('validationModal.policeAttestation')}>
          {[assure?.numeroPolice, assure?.numeroAttestation].filter(Boolean).join(' · ') || '-'}
        </Descriptions.Item>
      </Descriptions>

      <Form form={form} layout="vertical" style={{ marginTop: 20 }}>
        <Form.Item
          name="confirme"
          valuePropName="checked"
          rules={[{
            validator: (_, v) => v
              ? Promise.resolve()
              : Promise.reject(new Error(t('validationModal.cochezPourConfirmer'))),
          }]}
        >
          <Checkbox>
            {t('validationModal.confirmation')}
          </Checkbox>
        </Form.Item>
      </Form>
    </CBModal>
  );
}
